/**
 * Tri-Cube Coordinate System
 *
 * Three-axis coordinate system for triangle grids based on
 * BorisTheBrave's triangle grid approach.
 *
 * Axes a, b, c are defined by three parallel line families at 60° separation.
 * Constraint: a + b + c = 2 (up △) or a + b + c = 1 (down ▽)
 *
 * Pure math — zero DOM dependencies.
 */

import type { TriCubeCoord, PatternFn } from './types';
import { TRINITY_PALETTE } from './types';

const SQRT3 = Math.sqrt(3);

// --- Orientation ---

export function orientation(coord: TriCubeCoord): 'up' | 'down' {
  const sum = coord.a + coord.b + coord.c;
  return sum === 2 ? 'up' : 'down';
}

// --- Validation ---

export function isValid(coord: TriCubeCoord): boolean {
  const sum = coord.a + coord.b + coord.c;
  return (
    Number.isInteger(coord.a) &&
    Number.isInteger(coord.b) &&
    Number.isInteger(coord.c) &&
    (sum === 1 || sum === 2)
  );
}

// --- Conversions: TriCube ↔ Axial (row, col) ---

export function toAxial(coord: TriCubeCoord): { row: number; col: number } {
  const { a, b, c } = coord;
  const sum = a + b + c;
  const isDown = sum === 1;
  const row = a - 1;
  // col derivation: b encodes the diagonal strip position
  // For up triangles (sum=2): col = 2*(b-1) + row  (even parity: row+col even)
  // For down triangles (sum=1): col = 2*b - 1 + row (odd parity: row+col odd)
  const col = isDown ? 2 * b - 1 + row : 2 * (b - 1) + row;
  return { row, col };
}

export function fromAxial(row: number, col: number): TriCubeCoord {
  const isUp = (row + col) % 2 === 0;
  const a = row + 1;
  // Invert the toAxial col formula:
  // up:   col = 2*(b-1) + row  =>  b = (col - row) / 2 + 1
  // down: col = 2*b - 1 + row  =>  b = (col - row + 1) / 2
  const b = isUp ? (col - row) / 2 + 1 : (col - row + 1) / 2;
  const c = (isUp ? 2 : 1) - a - b;
  return { a, b, c };
}

// --- Conversions: TriCube ↔ Pixel ---

/**
 * Triangle center from tri-cube coords via axial conversion.
 * Origin is at top-left of grid (row=0, col=0).
 * Used by legacy modules (mosaic, icon editor, image-to-triangles).
 */
export function toPixel(
  coord: TriCubeCoord,
  sideLength: number,
): { x: number; y: number } {
  const { row, col } = toAxial(coord);
  const h = (sideLength * SQRT3) / 2;
  const isUp = (row + col) % 2 === 0;
  const x = col * (sideLength / 2);
  const y = row * h + (isUp ? (2 * h) / 3 : h / 3);
  return { x, y };
}

/**
 * Triangle center using Boris the Brave's direct formula.
 * Origin vertex (where lanes 0 and 1 meet) is at pixel (0,0).
 * This is the correct function for the Trinity System central-origin canvas.
 *
 * Formula: x = (0.5*a - 0.5*c) * s
 *          y = (-√3/6*a + √3/3*b - √3/6*c) * s
 */
export function toPixelCentered(
  coord: TriCubeCoord,
  sideLength: number,
): { x: number; y: number } {
  const { a, b, c } = coord;
  // Boris's formula uses y-UP (math convention); negate y for screen (y-DOWN)
  return {
    x: (0.5 * a - 0.5 * c) * sideLength,
    y: (SQRT3 / 6 * a - SQRT3 / 3 * b + SQRT3 / 6 * c) * sideLength,
  };
}

/**
 * Pick the triangle containing a cartesian point (Boris the Brave's formula).
 * Pixel (0,0) is the origin vertex.
 *
 * Formula: a = ceil(( x - √3/3 * y) / s)
 *          b = floor(√3*2/3 * y / s) + 1
 *          c = ceil((-x - √3/3 * y) / s)
 */
export function fromPixelCentered(
  x: number,
  y: number,
  sideLength: number,
): TriCubeCoord {
  // Screen uses y-DOWN; Boris's formula expects y-UP, so substitute -y
  return {
    a: Math.ceil(( x + SQRT3 / 3 * y) / sideLength),
    b: Math.floor(-SQRT3 * 2 / 3 * y / sideLength) + 1,
    c: Math.ceil((-x + SQRT3 / 3 * y) / sideLength),
  };
}

export function fromPixel(
  x: number,
  y: number,
  sideLength: number,
): TriCubeCoord {
  const h = (sideLength * SQRT3) / 2;
  const approxRow = Math.floor(y / h);
  const approxCol = Math.floor(x / (sideLength / 2));
  return fromAxial(approxRow, approxCol);
}

// --- Symmetry Operations (D₃ group) ---

export function rotate120(coord: TriCubeCoord): TriCubeCoord {
  return { a: coord.c, b: coord.a, c: coord.b };
}

export function rotate240(coord: TriCubeCoord): TriCubeCoord {
  return { a: coord.b, b: coord.c, c: coord.a };
}

export function reflectA(coord: TriCubeCoord): TriCubeCoord {
  return { a: coord.a, b: coord.c, c: coord.b };
}

export function reflectB(coord: TriCubeCoord): TriCubeCoord {
  return { a: coord.c, b: coord.b, c: coord.a };
}

export function reflectC(coord: TriCubeCoord): TriCubeCoord {
  return { a: coord.b, b: coord.a, c: coord.c };
}

export const SYMMETRIES: ((coord: TriCubeCoord) => TriCubeCoord)[] = [
  (c) => c, // identity
  rotate120,
  rotate240,
  reflectA,
  reflectB,
  reflectC,
];

// --- Distance ---

export function triDistance(a: TriCubeCoord, b: TriCubeCoord): number {
  return (
    Math.abs(a.a - b.a) + Math.abs(a.b - b.b) + Math.abs(a.c - b.c)
  );
}

// --- Neighbors ---

export function neighbors(coord: TriCubeCoord): TriCubeCoord[] {
  const sum = coord.a + coord.b + coord.c;
  if (sum === 2) {
    // Up triangle: subtract 1 from each axis
    return [
      { a: coord.a - 1, b: coord.b, c: coord.c },
      { a: coord.a, b: coord.b - 1, c: coord.c },
      { a: coord.a, b: coord.b, c: coord.c - 1 },
    ];
  } else {
    // Down triangle: add 1 to each axis
    return [
      { a: coord.a + 1, b: coord.b, c: coord.c },
      { a: coord.a, b: coord.b + 1, c: coord.c },
      { a: coord.a, b: coord.b, c: coord.c + 1 },
    ];
  }
}

// --- Ring / Region ---

export function disc(center: TriCubeCoord, radius: number): TriCubeCoord[] {
  const results: TriCubeCoord[] = [];
  // Brute-force all coords in a bounding box and filter by distance
  for (let da = -radius; da <= radius; da++) {
    for (let db = -radius; db <= radius; db++) {
      for (const sum of [1, 2]) {
        const dc = sum - (center.a + da) - (center.b + db);
        const candidate: TriCubeCoord = {
          a: center.a + da,
          b: center.b + db,
          c: dc,
        };
        // Verify sum constraint
        if (candidate.a + candidate.b + candidate.c !== sum) continue;
        if (triDistance(center, candidate) <= radius) {
          results.push(candidate);
        }
      }
    }
  }
  return results;
}

export function ring(center: TriCubeCoord, radius: number): TriCubeCoord[] {
  if (radius === 0) return [center];
  return disc(center, radius).filter(
    (c) => triDistance(center, c) === radius,
  );
}

// --- Color Helpers ---

function lerpColor(c1: string, c2: string, t: number): string {
  const parse = (hex: string) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  };
  const [r1, g1, b1] = parse(c1);
  const [r2, g2, b2] = parse(c2);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

// --- Preset Pattern Functions ---

const LIGHT_BLUE = TRINITY_PALETTE.lightBlue;
const MEDIUM_BLUE = TRINITY_PALETTE.mediumBlue;
const DARK_BLUE = TRINITY_PALETTE.darkBlue;
const DEEP_BLUE = TRINITY_PALETTE.deepBlue;

export const PATTERNS: Record<
  string,
  { name: string; description: string; fn: PatternFn }
> = {
  stripes: {
    name: 'Stripes',
    description: 'Horizontal stripes based on the a-axis',
    fn: (a, _b, _c) => (a % 2 === 0 ? LIGHT_BLUE : null),
  },
  diagonalA: {
    name: 'Diagonal A',
    description: 'Diagonal stripes on the b-axis',
    fn: (_a, b, _c) => (b % 2 === 0 ? MEDIUM_BLUE : null),
  },
  diagonalB: {
    name: 'Diagonal B',
    description: 'Diagonal stripes on the c-axis',
    fn: (_a, _b, c) => (c % 2 === 0 ? DARK_BLUE : null),
  },
  checkerboard: {
    name: 'Checkerboard',
    description: 'Up vs down orientation pattern',
    fn: (a, b, c) => (a + b + c === 2 ? LIGHT_BLUE : DEEP_BLUE),
  },
  hexagonal: {
    name: 'Hexagonal',
    description: 'Hexagonal groupings via modular sum',
    fn: (a, b, _c) => ((a + b) % 3 === 0 ? LIGHT_BLUE : null),
  },
  trinity: {
    name: 'Trinity',
    description: '3-fold symmetric: color by dominant axis',
    fn: (a, b, c) => {
      const m = Math.max(a, b, c);
      if (a === m) return LIGHT_BLUE;
      if (b === m) return MEDIUM_BLUE;
      return DARK_BLUE;
    },
  },
  rings: {
    name: 'Rings',
    description: 'Concentric rings from origin',
    fn: (a, b, c) => {
      const d = Math.abs(a) + Math.abs(b) + Math.abs(c);
      return d % 4 < 2 ? LIGHT_BLUE : null;
    },
  },
  gradient: {
    name: 'Gradient',
    description: 'Smooth gradient along the a-axis',
    fn: (a, _b, _c) => {
      const t = Math.min(Math.max(a / 10, 0), 1);
      return lerpColor(DEEP_BLUE, LIGHT_BLUE, t);
    },
  },
  sierpinski: {
    name: 'Sierpinski',
    description: 'Recursive fractal pattern',
    fn: (a, b, c) => {
      let aa = Math.abs(a),
        bb = Math.abs(b),
        cc = Math.abs(c);
      while (aa > 0 || bb > 0 || cc > 0) {
        if (aa % 2 + bb % 2 + cc % 2 > 2) return null;
        aa = Math.floor(aa / 2);
        bb = Math.floor(bb / 2);
        cc = Math.floor(cc / 2);
      }
      return LIGHT_BLUE;
    },
  },
};

// --- Icon Pattern Functions ---
// Each defines a shape within the icon grid (16 rows × 32 cols).
// Center of grid in axial: row≈8, col≈16 → tri-cube a≈9.

const ICON_CENTER_ROW = 7.5;
const ICON_CENTER_COL = 15.5;

export const ICON_PATTERNS: Record<
  string,
  { name: string; description: string; fn: PatternFn }
> = {
  apple: {
    name: 'Apple',
    description: 'Circular body with stem and leaf',
    fn: (a, b, c) => {
      const { row, col } = toAxial({ a, b, c });
      const dx = (col - ICON_CENTER_COL) / 7;
      const dy = (row - ICON_CENTER_ROW) / 6;
      const d = dx * dx + dy * dy;
      // Stem: narrow column above center
      if (row >= 1 && row <= 3 && Math.abs(col - ICON_CENTER_COL) < 1.5) {
        return DARK_BLUE;
      }
      // Leaf: small offset ellipse
      if (row >= 1 && row <= 4) {
        const lx = (col - ICON_CENTER_COL - 3) / 3;
        const ly = (row - 2) / 2;
        if (lx * lx + ly * ly < 1) return MEDIUM_BLUE;
      }
      // Body: two overlapping circles for apple shape
      const d1 = ((col - ICON_CENTER_COL + 2) / 7) ** 2 + ((row - 9) / 6) ** 2;
      const d2 = ((col - ICON_CENTER_COL - 2) / 7) ** 2 + ((row - 9) / 6) ** 2;
      if (d1 < 1 || d2 < 1) {
        // Notch at top center
        if (row < 6 && Math.abs(col - ICON_CENTER_COL) < 2) return null;
        return LIGHT_BLUE;
      }
      return null;
    },
  },
  star: {
    name: 'Star',
    description: '5-pointed star using angular math',
    fn: (a, b, c) => {
      const { row, col } = toAxial({ a, b, c });
      const dx = col - ICON_CENTER_COL;
      const dy = row - ICON_CENTER_ROW;
      const angle = Math.atan2(dy, dx);
      const dist = Math.sqrt(dx * dx + dy * dy);
      // 5-pointed star: radius oscillates with 5-fold symmetry
      const starR = 5 + 3 * Math.cos(5 * angle + Math.PI / 2);
      if (dist < starR) return LIGHT_BLUE;
      return null;
    },
  },
  heart: {
    name: 'Heart',
    description: 'Cardioid-inspired heart shape',
    fn: (a, b, c) => {
      const { row, col } = toAxial({ a, b, c });
      // Parametric heart: (x/7)^2 + ((y/6) - (|x/7|)^(2/3))^2 < 1
      const x = (col - ICON_CENTER_COL) / 7;
      const y = (ICON_CENTER_ROW + 1 - row) / 6; // flip y so heart points down
      const val = x * x + (y - Math.pow(Math.abs(x), 2 / 3)) ** 2;
      if (val < 1) return LIGHT_BLUE;
      return null;
    },
  },
  diamond: {
    name: 'Diamond',
    description: 'Rhombus shape via absolute value sum',
    fn: (a, b, c) => {
      const { row, col } = toAxial({ a, b, c });
      const dx = Math.abs(col - ICON_CENTER_COL) / 8;
      const dy = Math.abs(row - ICON_CENTER_ROW) / 7;
      if (dx + dy < 1) {
        // Inner/outer ring effect
        const t = dx + dy;
        return t < 0.5 ? LIGHT_BLUE : MEDIUM_BLUE;
      }
      return null;
    },
  },
};
